// 치과 「예약」(케어 앱 예약 요청·빈자리 알림 대기) — Cloud Functions 호출 모음 (2026-10-11, 플래그 careBooking strict OFF 출고)
// 계획 docs/계획_케어앱_예약대기_2026-10-11.md §2 S3·W1 (대표 10-11 승인). 법무 docs/법무검토_케어앱_예약대기_2026-10-11.md §4-5.
// 서버 계약: docs/계약_케어앱_예약대기_서버_2026-10-11.md §2(초안, 10-11 대조 완료 — 최종본에서 키 철자가 바뀌면 이 파일 한 곳만 고친다).
//   화면 컴포넌트는 CF 이름·응답 형태를 직접 알지 않는다(FN 표·normalize*).
// 원칙: 빈자리 알림 문구는 서버 고정(자유 문구 파라미터 없음 — 법무 C-4). 받지 못함 사유는 고정 키 4종(자유 입력 없음).
// ⚠️ 콘솔에는 오류 코드만 남긴다 — 환자명·희망 일시·방문 이유·전화번호는 절대 로그에 쓰지 않는다.
import { getFunctions, httpsCallable } from 'firebase/functions';
import { isAppCheckError, APP_CHECK_REQUIRED_TEXT } from '../utils/appCheckError';
import { RECEPTION_REASONS } from '../utils/deskReception';

const REGION = 'asia-northeast3';
const call = (name) => httpsCallable(getFunctions(undefined, REGION), name);

// 서버 함수 이름(계획서 §2 S3) — 계약이 다르면 여기만 바꾼다
export const BOOKING_FN = Object.freeze({
    listRequests: 'listClinicBookingRequests',
    decide: 'decideClinicBooking',
    listSchedule: 'listClinicBookingSchedule',
    getSettings: 'getClinicBookingSettings',
    setSettings: 'setClinicBookingSettings',
    sendOffer: 'sendClinicBookingOffer',
    closeOffer: 'closeClinicBookingOffer',
    listOffers: 'listClinicBookingOffers',
    inboxCount: 'getClinicBookingInboxCount',
    markVerified: 'markPatientVerified',
});

export const BOOKING_GENERIC_ERROR = '요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.';

// ── 고정 문구 (정본 docs/정본_케어앱_예약대기_문서묶음_2026-10-11.md §5-2 글자 그대로) ──
export const BOOKING_HEAD_NOTE = '확정해야 예약이 됩니다. 덴탈클러스터는 예약을 정하지 않습니다.';
export const OFFER_FIXED_NOTE = '알림 문구는 고정입니다(할인·재촉 문구를 넣을 수 없습니다).';
export const EMR_NOTE = '전자차트(EMR)에도 같은 일정을 적어 주세요.';
export const CHART_NOTED_LABEL = '차트 반영';
export const SETTINGS_LOCK_NOTE = '환자 앱 등록 처리위탁 확인(새 판)을 대표 계정으로 확인한 뒤 켤 수 있습니다.';
export const offerConfirmText = (n) => `대기 중 ${n}명에게 알립니다. 먼저 고른 한 분으로 정해집니다.`;
export const VERIFY_NOTE = '본원에 오신 적 있는 분인지 확인한 뒤 누르세요. 확인한 분만 앱에서 예약 요청을 할 수 있습니다.';
// 환자 앱 빈자리 카드 문구(법무 §4-5 그대로) — 치과 화면에는 "환자 앱에 이렇게 보입니다" 미리보기로만 쓴다(서버가 정본)
export const offerAppText = (clinicName, day, time) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(day || ''));
    const md = m ? `${Number(m[2])}월 ${Number(m[3])}일` : String(day || '');
    return `${clinicName}에 ${md} ${time || ''} 빈자리가 있습니다. 대기 중인 여러 분께 함께 알려 드렸으며, 먼저 고르신 한 분으로 정해집니다.`;
};

// ── 코드표 ──
export const REASON_LABEL = Object.freeze(Object.fromEntries(RECEPTION_REASONS.map(r => [r.key, r.label])));   // 검진·치료·상담·기타(데스크 접수와 같은 값 — Q7)
export const reasonLabel = (k) => REASON_LABEL[k] || '-';
export const PART_LABEL = Object.freeze({ am: '오전', pm: '오후', any: '상관없음' });
export const partLabel = (k) => PART_LABEL[k] || '-';
// 확정 시각 기본값 — 희망은 날짜+오전/오후뿐이라 시각은 직원이 고친다(빈칸으로 두지 않아 클릭 1번+[확정]이 되게)
export const DEFAULT_TIME_OF_PART = Object.freeze({ am: '10:00', pm: '14:00', any: '10:00' });
export const DECLINE_REASONS = Object.freeze([
    { key: 'full', label: '예약이 찼습니다' },
    { key: 'closed_day', label: '휴진일입니다' },
    { key: 'not_our_patient', label: '본원 환자가 아닙니다' },
    { key: 'other', label: '기타' },
]);
export const declineLabel = (k) => (DECLINE_REASONS.find(d => d.key === k) || {}).label || '-';
export const STATUS_LABEL = Object.freeze({
    requested: '확정 전', proposed: '다른 시간 제안함(환자 답 대기)', confirmed: '확정', phone: '전화로 안내',
    declined: '받지 못함', declined_by_patient: '환자가 제안 시간을 거절함', cancelled: '취소됨', expired: '희망 날짜 지남',
});
export const statusLabel = (s) => STATUS_LABEL[s] || s || '-';
export const OFFER_STATUS_LABEL = Object.freeze({ open: '열림', taken: '선택됨', closed: '닫음', expired: '마감' });
export const offerStatusLabel = (s) => OFFER_STATUS_LABEL[s] || s || '-';
export const OFFER_WINDOW_OPTIONS = Object.freeze([15, 30, 60]);   // B4 — 기본 30분, 진료 30분 전 자동 마감(서버)
export const DAILY_CAP_MAX = 100;
export const WEEKDAYS = Object.freeze(['일', '월', '화', '수', '목', '금', '토']);   // 0=일 … 6=토 (JS getDay)

// ── 날짜 보조(순수) ──
const pad2 = (n) => String(n).padStart(2, '0');
export const dayKeyOf = (d = new Date()) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
export const addDays = (key, n) => { const d = new Date(`${key}T00:00:00`); d.setDate(d.getDate() + n); return dayKeyOf(d); };
export const fmtDay = (key) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(key || ''));
    if (!m) return String(key || '');
    const wd = WEEKDAYS[new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getDay()];
    return `${Number(m[2])}/${Number(m[3])}(${wd})`;
};
export const isTime = (t) => /^([01]\d|2[0-3]):[0-5]\d$/.test(String(t || ''));
export const isDayKey = (k) => /^\d{4}-\d{2}-\d{2}$/.test(String(k || ''));
const toMs = (v) => {
    if (v == null) return null;
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    if (typeof v === 'string') { const t = Date.parse(v); return Number.isFinite(t) ? t : null; }
    if (typeof v.toMillis === 'function') return v.toMillis();
    if (typeof v._seconds === 'number') return v._seconds * 1000;
    if (typeof v.seconds === 'number') return v.seconds * 1000;
    return null;
};
export const fmtMs = (ms) => {
    if (!ms) return '';
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getMonth() + 1}/${d.getDate()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};
const slotOf = (s) => (s && isDayKey(s.day) ? { day: s.day, time: isTime(s.time) ? s.time : '' } : null);

// ── 응답 정규화(없으면 안전한 빈 상태) ──
export function normalizeBooking(b) {
    const x = b || {};
    const wishes = Array.isArray(x.wishes) ? x.wishes.filter(w => w && isDayKey(w.day)).slice(0, 3).map(w => ({ day: w.day, part: PART_LABEL[w.part] ? w.part : 'any' })) : [];
    return {
        bookingId: String(x.bookingId || x.id || ''),
        pid: String(x.pid || ''),
        name: String(x.name || x.patientName || ''),
        wishes,
        reason: x.reason || '',
        waitlist: x.waitlist === true && !x.waitlistEndedAt,
        status: x.status || '',
        slot: slotOf(x.slot),
        proposed: slotOf(x.proposed),
        movedFrom: slotOf(x.movedFrom),
        declineReason: x.declineReason || '',
        cancelledBy: x.cancelledBy || '',
        chartNoted: x.chartNoted === true,
        registeredVia: x.registeredVia || '',
        // 등록 경로 배지 — QR 등록이면 'QR', 치과가 확인한 등록이면 '치과 확인'(P1-3). 둘 다 아니면 서버가 애초에 요청을 받지 않는다
        verified: x.verified === true || toMs(x.clinicVerifiedAt) != null,
        createdAt: toMs(x.createdAt),
        decidedAt: toMs(x.decidedAt),
        updatedAt: toMs(x.updatedAt),
    };
}
export const routeBadgeOf = (b) => (b.registeredVia === 'care_qr' ? 'QR 등록' : b.verified ? '치과 확인' : '');

export function normalizeOffer(o) {
    const x = o || {};
    return {
        offerId: String(x.offerId || x.id || ''),
        slot: slotOf(x.slot),
        status: x.status || '',
        targetCount: Number.isInteger(x.targetCount) ? x.targetCount : (Array.isArray(x.targets) ? x.targets.length : 0),
        expiresAt: toMs(x.expiresAt),
        createdAt: toMs(x.createdAt),
        takenAt: toMs(x.takenAt),
    };
}

// 계약 §2-1 — 기본값 enabled false · days 월~토 · parts am/pm · dailyCap 20 · offerWindowMin 30 · notifyNew true
export function normalizeSettings(d) {
    const x = d || {};
    const days = Array.isArray(x.days) ? x.days.filter(n => Number.isInteger(n) && n >= 0 && n <= 6) : [1, 2, 3, 4, 5, 6];
    const rc = x.roadmapClosedDays || {};
    const parts = Array.isArray(x.parts) ? x.parts.filter(p => p === 'am' || p === 'pm') : ['am', 'pm'];
    return {
        available: x.available === true,
        enabled: x.enabled === true,
        days,
        parts,
        dailyCap: Number.isInteger(x.dailyCap) ? x.dailyCap : 20,
        offerWindowMin: OFFER_WINDOW_OPTIONS.includes(x.offerWindowMin) ? x.offerWindowMin : 30,
        closedDays: Array.isArray(x.closedDays) ? x.closedDays.filter(isDayKey).sort() : [],
        // 질문 로드맵 설정의 휴진(읽기 전용 — 서버가 요청 검증 때 합집합으로 쓴다) {weekly:[0..6], dates:['YYYY-MM-DD']}
        roadmapClosedDays: Array.isArray(rc.dates) ? rc.dates.filter(isDayKey).sort() : (Array.isArray(rc) ? rc.filter(isDayKey).sort() : []),
        roadmapClosedWeekly: Array.isArray(rc.weekly) ? rc.weekly.filter(n => Number.isInteger(n) && n >= 0 && n <= 6) : [],
        notifyNew: x.notifyNew !== false,
        paClinicReady: x.paClinicReady === true,
        consentReady: x.consentReady === true,
        flagOn: x.flagOn === true,
        isOwner: x.isOwner === true,
        clinicName: typeof x.clinicName === 'string' ? x.clinicName : '',   // 계약 2-1 에는 없음 — 없으면 화면이 계정 이름으로 대신
    };
}

// ── 오류 → 화면 문구 ──
// 계약 §2 details.code
const CODE_TEXT = Object.freeze({
    'booking-off': '예약 기능이 아직 열리지 않았습니다.',
    'feature-off': '예약 기능이 아직 열리지 않았습니다.',
    'booking-state': '이미 처리되었거나 환자가 취소한 요청입니다. 화면을 새로고침해 주세요.',
    'owner-only': '치과 대표만 할 수 있습니다.',
    'booking-pa-required': '환자 앱 등록 처리위탁 확인(새 판)을 대표 계정으로 확인한 뒤 켤 수 있습니다.',
    'settings-invalid': '설정 값이 올바르지 않습니다. 요일·오전/오후를 하나 이상 고르고 하루 상한은 1~100건으로 넣어 주세요.',
    'offer-slot-invalid': '빈자리 날짜·시간이 올바르지 않습니다.',
    'offer-too-late': '30분 안에 시작하는 시간은 알릴 수 없습니다.',
    'offer-no-targets': '알림을 받을 대기 환자가 없습니다.',
    'access-log-failed': '잠시 후 다시 시도해 주세요.',
    // 10-11 계약 변경(머리 4·5) ☆
    'offer-duplicate': '같은 날짜·시간에 이미 열려 있는 빈자리 알림이 있습니다. 「빈자리」 탭에서 확인해 주세요.',
    'rate-limit-unavailable': '잠시 처리할 수 없습니다. 조금 뒤 다시 시도해 주세요.',
});

export function describeBookingError(e, fallback = BOOKING_GENERIC_ERROR) {
    console.error('[bookingApi]', e?.code || '', e?.details?.code || '');
    if (isAppCheckError(e)) return APP_CHECK_REQUIRED_TEXT;
    const c = e?.details?.code;
    if (typeof c === 'string' && CODE_TEXT[c]) return CODE_TEXT[c];
    if (e?.code === 'functions/unauthenticated') return '로그인이 필요합니다.';
    if (e?.code === 'functions/permission-denied') return e?.message || '권한이 없습니다.';
    if (e?.code === 'functions/resource-exhausted') return '요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.';
    if (e?.code === 'functions/not-found') return '서버 기능이 아직 준비되지 않았거나 대상을 찾을 수 없습니다. 화면을 새로고침해 주세요.';
    if (e?.code === 'functions/invalid-argument') return e?.message || '입력 값이 올바르지 않습니다.';
    if (e?.code === 'functions/failed-precondition' || e?.code === 'functions/unavailable') return e?.message || fallback;
    return fallback;
}

// ── 설정 조회 공유 캐시(메뉴·화면·환자 상세가 같은 응답을 30초간 재사용 — 네트워크 1회) ──
const SETTINGS_SHARE_MS = 30 * 1000;
let settingsShared = { promise: null, data: null, at: 0 };
const settingsListeners = new Set();

export async function getClinicBookingSettings() {
    const res = await call(BOOKING_FN.getSettings)({});
    return normalizeSettings(res?.data);
}
export function getClinicBookingSettingsShared({ force = false } = {}) {
    const now = Date.now();
    if (!force && settingsShared.data && now - settingsShared.at < SETTINGS_SHARE_MS) return Promise.resolve(settingsShared.data);
    if (!force && settingsShared.promise) return settingsShared.promise;
    const p = getClinicBookingSettings()
        .then(d => { settingsShared = { promise: null, data: d, at: Date.now() }; return d; })
        .catch(e => { settingsShared = { promise: null, data: null, at: 0 }; throw e; });
    settingsShared.promise = p;
    return p;
}
export function invalidateClinicBookingSettings() {
    settingsShared = { promise: null, data: null, at: 0 };
    settingsListeners.forEach(fn => { try { fn(); } catch (e) { console.error('[bookingApi] settings listener 실패:', e?.message || e); } });
}
export function subscribeClinicBookingSettings(fn) {
    settingsListeners.add(fn);
    return () => settingsListeners.delete(fn);
}

// 대표만. payload = {enabled, days, parts, dailyCap, offerWindowMin, closedDays, notifyNew} → {ok, settings}
export async function setClinicBookingSettings(payload) {
    const res = await call(BOOKING_FN.setSettings)(payload);
    invalidateClinicBookingSettings();
    return res?.data || { ok: false };
}

// ── 요청함 ── status: 'new'|'open'|'done'
export async function listClinicBookingRequests({ status = 'new', cursor } = {}) {
    const payload = { status };
    if (cursor != null) payload.cursor = cursor;
    const res = await call(BOOKING_FN.listRequests)(payload);
    const d = res?.data || {};
    return {
        items: (Array.isArray(d.items) ? d.items : []).map(normalizeBooking),
        nextCursor: d.nextCursor ?? null,
    };
}

// 계약 2-4 action: 'confirm'|'propose'|'phone'|'decline'|'cancel'(확정 예약 치과 취소)|'chart_noted'(차트 반영 표시만)
export async function decideClinicBooking({ pid, bookingId, action, slot, declineReason, chartNoted }) {
    const payload = { pid, bookingId, action };
    if (slot) payload.slot = { day: slot.day, time: slot.time };
    if (declineReason) payload.declineReason = declineReason;
    if (typeof chartNoted === 'boolean') payload.chartNoted = chartNoted;
    const res = await call(BOOKING_FN.decide)(payload);
    return res?.data || { ok: false };
}

export async function setClinicBookingChartNoted({ pid, bookingId, chartNoted }) {
    return decideClinicBooking({ pid, bookingId, action: 'chart_noted', chartNoted: chartNoted === true });
}

// ── 일정 ── from·to = 'YYYY-MM-DD'
export async function listClinicBookingSchedule({ from, to }) {
    const res = await call(BOOKING_FN.listSchedule)({ from, to });
    const d = res?.data || {};
    return { items: (Array.isArray(d.items) ? d.items : []).map(normalizeBooking) };
}

// ── 빈자리 ── 문구 파라미터 없음(C-4). targets 생략 = 서버 기본(대기 신청자 전원, 최대 100)
//   dryRun:true = 대상 미리보기 {targetCount, targets:[{bookingId, name}]} (접속기록 booking_offer_preview)
export async function sendClinicBookingOffer({ slot, targets, dryRun = false }) {
    const payload = { slot: { day: slot.day, time: slot.time } };
    if (Array.isArray(targets)) payload.targets = targets;
    if (dryRun) payload.dryRun = true;
    const res = await call(BOOKING_FN.sendOffer)(payload);
    return res?.data || { ok: false };
}
export async function previewClinicBookingOffer(slot) {
    const d = await sendClinicBookingOffer({ slot, dryRun: true });
    const targets = (Array.isArray(d?.targets) ? d.targets : []).filter(t => t && t.bookingId).map(t => ({ bookingId: String(t.bookingId), name: String(t.name || '') }));
    return { targetCount: Number.isInteger(d?.targetCount) ? d.targetCount : targets.length, targets };
}
export async function listClinicBookingOffers() {
    const res = await call(BOOKING_FN.listOffers)({});
    const d = res?.data || {};
    return { items: (Array.isArray(d.items) ? d.items : []).map(normalizeOffer) };
}
export async function closeClinicBookingOffer(offerId) {
    const res = await call(BOOKING_FN.closeOffer)({ offerId });
    return res?.data || { ok: false };
}

// ── 배지(건수만 — 이름 0) ──
export async function getClinicBookingInboxCount() {
    const res = await call(BOOKING_FN.inboxCount)({});
    const d = res?.data || {};
    return {
        newCount: Number.isInteger(d.newCount) ? d.newCount : (Number.isInteger(d.count) ? d.count : 0),
        waitlistCount: Number.isInteger(d.waitlistCount) ? d.waitlistCount : 0,
    };
}

// ── 환자 상세 [본원 환자 확인] ── clear:true = 확인 해제(대표만)
export async function markPatientVerified(pid, { clear = false } = {}) {
    const payload = { pid };
    if (clear) payload.clear = true;
    const res = await call(BOOKING_FN.markVerified)(payload);
    return res?.data || { ok: false };
}

